export const canUseDOM = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
);

let cachedScrollbarSize: number | undefined;
export function scrollbarSize(recalc?: boolean) {
  if ((!cachedScrollbarSize && cachedScrollbarSize !== 0) || recalc) {
    if (canUseDOM) {
      const scrollDiv = document.createElement('div');

      scrollDiv.style.position = 'absolute';
      scrollDiv.style.top = '-9999px';
      scrollDiv.style.width = '50px';
      scrollDiv.style.height = '50px';
      scrollDiv.style.overflow = 'scroll';

      document.body.appendChild(scrollDiv);
      cachedScrollbarSize = scrollDiv.offsetWidth - scrollDiv.clientWidth;
      document.body.removeChild(scrollDiv);
    }
  }

  return cachedScrollbarSize ?? 0;
}

/**
 * @returns Walks up the node hierarchy looking for `componentNode`. Returns `true` if found, or
 * `Document` if it walks all the way up without a match.
 */
export function findHighest(
  current: Node,
  componentNode: Node,
  ignoredClasses: Array<string> = []
): Node | true {
  if (current === componentNode) {
    return true;
  }

  const hasIgnoredClass = (classList?: DOMTokenList) =>
    !classList || ignoredClasses.some(c => classList.contains(c));

  while (current.parentNode) {
    if (
      current.parentNode &&
      (current === componentNode || hasIgnoredClass((current as HTMLElement).classList))
    ) {
      return true;
    }

    current = current.parentNode;
  }

  return current;
}

/**
 * Downloads an image and extracts it into a data URL.
 * @param src URL to the image.
 * @param cors Set the cross-origin mode of the image if loading images from a different origin
 * (for example Clearbit). Note that if this is set, the remote server must set the appropriate
 * CORS headers as well:
 * - https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-crossorigin
 * - https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image
 * @returns Data URL of the image, or `null` if errors occurred during loading or extraction.
 */
export async function getImageAsDataUrl(
  src: string,
  cors: 'anonymous' | 'use-credentials' | null = null
): Promise<string | null> {
  return new Promise(resolve => {
    const img = document.createElement('img');
    img.hidden = true;
    img.decoding = 'sync';
    img.crossOrigin = cors;
    img.src = src;
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const context = canvas.getContext('2d');
        context?.drawImage(img, 0, 0);
        const data = canvas.toDataURL();
        resolve(data);
      } catch {
        resolve(null);
      } finally {
        document.body.removeChild(img);
      }
    };
    img.onerror = () => {
      resolve(null);
      document.body.removeChild(img);
    };
    document.body.appendChild(img);
  });
}
